import { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiBriefcase,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiSave,
  FiShield,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, EMPLOYEE_SALARY_VIEW_PERMISSION } from '../../utils/permissions';
import { preservePrivateSalary } from './employeeSalaryPrivacy';

const initialForm = {
  fullName: "",
  nickname: "",
  email: "",
  phone: "",
  gender: "",
  dateOfBirth: "",
  joiningDate: "",
  branchId: "",
  departmentId: "",
  designationId: "",
  employmentType: "PERMANENT",
  status: "ACTIVE",

  commissionPercent: "",

  salaryType: "",
  basicSalary: "",
  ctc: "",
  overtimeEligible: false,
  overtimeRate: "",
  earlyExitDeductionEligible: false,
  earlyExitDeductionRate: "",
  paymentMode: "",

  shiftType: "",
  shiftStartTime: "",
  shiftEndTime: "",
  breakDurationMinutes: "",
  graceMinutes: "",
  attendanceHolidayPolicyId: "",
  attendanceHolidayPolicyEffectiveFrom: "",

  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  ifscCode: "",

  aadhaarNumber: "",
  panNumber: "",

  grantErpAccess: false,
  erpRoleIds: [],
};

function toDateInput(value) {
  return value ? new Date(value).toISOString().slice(0, 10) : "";
}

export default function EditEmployee() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken, user } = useAuth();

  const [form, setForm] = useState(initialForm);
  const [employee, setEmployee] = useState(null);
  const canViewSalary = hasPermission(user, EMPLOYEE_SALARY_VIEW_PERMISSION) && employee?.salaryVisibility !== 'PRIVATE';
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [attendanceHolidayPolicies, setAttendanceHolidayPolicies] = useState(
    [],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  const erpRoles = useMemo(
    () => roles.filter((role) => role.code !== "EMPLOYEE_SELF_SERVICE"),
    [roles],
  );

  const selectedAttendanceHolidayPolicy = useMemo(
    () =>
      attendanceHolidayPolicies.find(
        (policy) => policy.id === form.attendanceHolidayPolicyId,
      ),
    [attendanceHolidayPolicies, form.attendanceHolidayPolicyId],
  );

  const selectedPolicyTiers = useMemo(
    () =>
      [...(selectedAttendanceHolidayPolicy?.tiers || [])].sort(
        (left, right) =>
          Number(left.minimumQualifyingDays) -
          Number(right.minimumQualifyingDays),
      ),
    [selectedAttendanceHolidayPolicy],
  );

  const aadhaarDocument = useMemo(
    () =>
      employee?.documents?.find(
        (document) => document.documentType === "AADHAAR",
      ) || null,
    [employee],
  );

  const panDocument = useMemo(
    () =>
      employee?.documents?.find(
        (document) => document.documentType === "PAN",
      ) || null,
    [employee],
  );

  useEffect(() => {
    async function loadPage() {
      if (!accessToken || !id) return;

      try {
        setIsLoading(true);
        setMessage("");

        const [
          employeeResponse,
          branchResponse,
          departmentResponse,
          roleResponse,
          attendanceHolidayPolicyResponse,
        ] = await Promise.all([
          apiRequest(`/employees/${id}`, { accessToken }),
          apiRequest("/branches", { accessToken }),
          apiRequest("/departments?activeOnly=true", { accessToken }),
          apiRequest("/roles", { accessToken }),
          apiRequest("/attendance-holiday-policies?activeOnly=true", {
            accessToken,
          }),
        ]);

        const primaryAssignment =
          employeeResponse.assignments?.find(
            (assignment) => assignment.isPrimary,
          ) || employeeResponse.assignments?.[0];

        const assignedErpRoleIds =
          employeeResponse.user?.roles
            ?.map((userRole) => userRole.role)
            .filter((role) => role?.id && role.code !== "EMPLOYEE_SELF_SERVICE")
            .map((role) => role.id) || [];

        const defaultCommissionRule = employeeResponse.commissionRules?.find(
          (rule) =>
            rule.name === "Default POS Commission" &&
            rule.scope === "ALL_PRODUCTS" &&
            rule.rateType === "PERCENT" &&
            rule.isActive !== false,
        );

        const compensation = employeeResponse.compensation || {};
        const workSchedule = employeeResponse.workSchedule || {};
        const bankAccount = employeeResponse.bankAccount || {};
        const currentPolicyAssignment =
          employeeResponse.attendanceHolidayPolicyAssignments?.find(
            (assignment) => !assignment.effectiveTo,
          ) || employeeResponse.attendanceHolidayPolicyAssignments?.[0];

        setEmployee(employeeResponse);
        setBranches(branchResponse || []);
        setDepartments(departmentResponse || []);
        setRoles(roleResponse || []);
        setAttendanceHolidayPolicies(attendanceHolidayPolicyResponse || []);

        setForm({
          fullName: employeeResponse.fullName || "",
          nickname: employeeResponse.nickname || "",
          email: employeeResponse.user?.email || "",
          phone: employeeResponse.user?.phone || "",
          gender: employeeResponse.gender || "",
          dateOfBirth: toDateInput(employeeResponse.dateOfBirth),
          joiningDate: toDateInput(employeeResponse.joiningDate),

          branchId: primaryAssignment?.branchId || "",
          departmentId: primaryAssignment?.departmentId || "",
          designationId: primaryAssignment?.designationId || "",
          employmentType: primaryAssignment?.employmentType || "PERMANENT",
          status: employeeResponse.status || "ACTIVE",

          commissionPercent:
            defaultCommissionRule?.rate !== undefined &&
            defaultCommissionRule?.rate !== null
              ? String(defaultCommissionRule.rate)
              : "",

          salaryType: compensation.salaryType || "",
          ctc: compensation.ctc == null ? "" : String(compensation.ctc),
          basicSalary:
            compensation.basicSalary !== undefined &&
            compensation.basicSalary !== null
              ? String(compensation.basicSalary)
              : "",
          overtimeEligible: Boolean(compensation.overtimeEligible),
          overtimeRate:
            compensation.overtimeRate !== undefined &&
            compensation.overtimeRate !== null
              ? String(compensation.overtimeRate)
              : "",
          earlyExitDeductionEligible: Boolean(
            compensation.earlyExitDeductionEligible,
          ),
          earlyExitDeductionRate:
            compensation.earlyExitDeductionRate !== undefined &&
            compensation.earlyExitDeductionRate !== null
              ? String(compensation.earlyExitDeductionRate)
              : "",
          paymentMode: compensation.paymentMode || "",

          shiftType: workSchedule.shiftType || "",
          shiftStartTime: workSchedule.shiftStartTime || "",
          shiftEndTime: workSchedule.shiftEndTime || "",
          breakDurationMinutes:
            workSchedule.breakDurationMinutes !== undefined &&
            workSchedule.breakDurationMinutes !== null
              ? String(workSchedule.breakDurationMinutes)
              : "",
          graceMinutes:
            workSchedule.graceMinutes !== undefined &&
            workSchedule.graceMinutes !== null
              ? String(workSchedule.graceMinutes)
              : "",
          attendanceHolidayPolicyId: currentPolicyAssignment?.policyId || "",
          attendanceHolidayPolicyEffectiveFrom: toDateInput(
            currentPolicyAssignment?.effectiveFrom,
          ),

          bankName: bankAccount.bankName || "",
          accountHolderName: bankAccount.accountHolderName || "",
          accountNumber: "",
          ifscCode: "",

          aadhaarNumber: "",
          panNumber: "",

          grantErpAccess: assignedErpRoleIds.length > 0,
          erpRoleIds: assignedErpRoleIds,
        });
      } catch (error) {
        setMessage(error.message || "Unable to load employee information.");
      } finally {
        setIsLoading(false);
      }
    }

    loadPage();
  }, [accessToken, id]);

  useEffect(() => {
    async function loadDesignations() {
      if (!accessToken || !form.departmentId) {
        setDesignations([]);
        return;
      }

      try {
        const response = await apiRequest(
          `/designations?activeOnly=true&departmentId=${form.departmentId}`,
          { accessToken },
        );

        setDesignations(response || []);
      } catch (error) {
        setMessage(error.message || "Unable to load designations.");
      }
    }

    loadDesignations();
  }, [accessToken, form.departmentId]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => {
      if (name === "departmentId") {
        return {
          ...previous,
          departmentId: value,
          designationId: "",
        };
      }

      if (name === "grantErpAccess") {
        return {
          ...previous,
          grantErpAccess: checked,
          erpRoleIds: checked ? previous.erpRoleIds : [],
        };
      }

      if (name === "erpRoleIds") {
        return {
          ...previous,
          erpRoleIds: Array.from(
            event.target.selectedOptions,
            (option) => option.value,
          ),
        };
      }

      return {
        ...previous,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const optionalNumber = (value) => (value === "" ? undefined : Number(value));

  const saveChanges = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!form.fullName.trim()) {
      setMessage("Full name is required.");
      return;
    }

    if (!form.email.trim() && !form.phone.trim()) {
      setMessage("At least one of email or phone number is required.");
      return;
    }

    if (
      !form.branchId ||
      !form.departmentId ||
      !form.designationId ||
      !form.joiningDate
    ) {
      setMessage(
        "Branch, department, designation, and joining date are required.",
      );
      return;
    }

    if (form.grantErpAccess && !form.erpRoleIds.length) {
      setMessage(
        "Select at least one ERP role or turn off ERP dashboard access.",
      );
      return;
    }

    const commissionPercent = optionalNumber(form.commissionPercent);
    const basicSalary = optionalNumber(form.basicSalary);
    const ctc = optionalNumber(form.ctc);
    if (ctc !== undefined && (!Number.isFinite(ctc) || ctc < 0)) {
      setMessage("Annual CTC must be a non-negative amount.");
      return;
    }
    const overtimeRate = optionalNumber(form.overtimeRate);
    const earlyExitDeductionRate = optionalNumber(form.earlyExitDeductionRate);
    const breakDurationMinutes = optionalNumber(form.breakDurationMinutes);
    const graceMinutes = optionalNumber(form.graceMinutes);

    if (
      commissionPercent !== undefined &&
      (!Number.isFinite(commissionPercent) ||
        commissionPercent < 0 ||
        commissionPercent > 100)
    ) {
      setMessage("POS commission must be between 0 and 100.");
      return;
    }

    if (
      basicSalary !== undefined &&
      (!Number.isFinite(basicSalary) || basicSalary < 0)
    ) {
      setMessage("Basic salary must be a valid positive amount.");
      return;
    }

    if (
      overtimeRate !== undefined &&
      (!Number.isFinite(overtimeRate) || overtimeRate < 0)
    ) {
      setMessage("Overtime rate must be a valid positive amount.");
      return;
    }

    if (
      earlyExitDeductionRate !== undefined &&
      (!Number.isFinite(earlyExitDeductionRate) || earlyExitDeductionRate < 0)
    ) {
      setMessage("Early exit deduction rate must be a valid positive amount.");
      return;
    }

    const payload = {
      fullName: form.fullName.trim(),
      ...(form.nickname.trim() ? { nickname: form.nickname.trim() } : {}),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),

      gender: form.gender || undefined,
      dateOfBirth: form.dateOfBirth || undefined,
      joiningDate: form.joiningDate,

      branchId: form.branchId,
      departmentId: form.departmentId,
      designationId: form.designationId,
      employmentType: form.employmentType,
      status: form.status,

      commissionPercent: commissionPercent ?? 0,

      ...(form.salaryType ? { salaryType: form.salaryType } : {}),
      ...(basicSalary !== undefined ? { basicSalary } : {}),
      ctc: ctc ?? null,
      overtimeEligible: form.overtimeEligible,
      ...(overtimeRate !== undefined ? { overtimeRate } : {}),
      earlyExitDeductionEligible: form.earlyExitDeductionEligible,
      ...(earlyExitDeductionRate !== undefined
        ? { earlyExitDeductionRate }
        : {}),
      ...(form.paymentMode ? { paymentMode: form.paymentMode } : {}),

      ...(form.shiftType.trim() ? { shiftType: form.shiftType.trim() } : {}),
      ...(form.shiftStartTime ? { shiftStartTime: form.shiftStartTime } : {}),
      ...(form.shiftEndTime ? { shiftEndTime: form.shiftEndTime } : {}),
      ...(breakDurationMinutes !== undefined ? { breakDurationMinutes } : {}),
      ...(graceMinutes !== undefined ? { graceMinutes } : {}),
      ...(form.attendanceHolidayPolicyId
        ? {
            attendanceHolidayPolicyId: form.attendanceHolidayPolicyId,
            attendanceHolidayPolicyEffectiveFrom:
              form.attendanceHolidayPolicyEffectiveFrom ||
              form.joiningDate ||
              undefined,
          }
        : {}),

      ...(form.bankName.trim() ? { bankName: form.bankName.trim() } : {}),
      ...(form.accountHolderName.trim()
        ? { accountHolderName: form.accountHolderName.trim() }
        : {}),
      ...(form.accountNumber.trim()
        ? { accountNumber: form.accountNumber.trim() }
        : {}),
      ...(form.ifscCode.trim()
        ? { ifscCode: form.ifscCode.trim().toUpperCase() }
        : {}),

      ...(form.aadhaarNumber.trim()
        ? { aadhaarNumber: form.aadhaarNumber.trim() }
        : {}),
      ...(form.panNumber.trim()
        ? { panNumber: form.panNumber.trim().toUpperCase() }
        : {}),

      // [] removes ERP dashboard access but retains EMPLOYEE_SELF_SERVICE.
      erpRoleIds: form.grantErpAccess ? form.erpRoleIds : [],
    };

    try {
      setIsSaving(true);

      await apiRequest(`/employees/${id}`, {
        method: "PATCH",
        body: preservePrivateSalary(payload, canViewSalary),
        accessToken,
      });

      navigate(`/admindashboard/employees/${id}`);
    } catch (error) {
      const backendMessage = Array.isArray(error.details?.message)
        ? error.details.message.join(", ")
        : error.message;

      setMessage(backendMessage || "Unable to update employee.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white py-20 text-center text-gray-500">
        Loading employee information...
      </div>
    );
  }

  return (
    <div className="pb-24">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/admindashboard/employees")}
            className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary-700"
          >
            <FiArrowLeft />
            Back to Employees
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Edit Employee</h1>

          <p className="mt-2 text-gray-500">
            Update employee profile, salary, working schedule, payroll settings,
            bank details, commission and system access.
          </p>
        </div>
      </div>

      {message ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {message}
        </div>
      ) : null}

      <form onSubmit={saveChanges} className="space-y-6">
        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">
            Personal Information
          </h2>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              label="Full Name"
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              required
            />

            <Field
              label="Nickname"
              name="nickname"
              value={form.nickname}
              onChange={handleChange}
              placeholder="Optional name used internally"
            />

            <Field
              label="Email Address"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
            />

            <Field
              label="Phone Number"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />

            <SelectField
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              options={[
                ["", "Select Gender"],
                ["MALE", "Male"],
                ["FEMALE", "Female"],
                ["OTHER", "Other"],
                ["PREFER_NOT_TO_SAY", "Prefer not to say"],
              ]}
            />

            <Field
              label="Date of Birth"
              type="date"
              name="dateOfBirth"
              value={form.dateOfBirth}
              onChange={handleChange}
            />

            <SelectField
              label="Employee Status"
              name="status"
              value={form.status}
              onChange={handleChange}
              options={[
                ["ACTIVE", "Active"],
                ["DRAFT", "Draft"],
                ["INACTIVE", "Inactive"],
                ["EXITED", "Exited"],
              ]}
            />
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiBriefcase size={20} />}
            title="Employment Details"
            description="Primary employment assignment and organisational position."
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <SelectField
              label="Branch"
              name="branchId"
              value={form.branchId}
              onChange={handleChange}
              required
              options={[
                ["", "Select Branch"],
                ...branches
                  .filter((branch) => branch.isActive)
                  .map((branch) => [branch.id, branch.name]),
              ]}
            />

            <SelectField
              label="Department"
              name="departmentId"
              value={form.departmentId}
              onChange={handleChange}
              required
              options={[
                ["", "Select Department"],
                ...departments.map((department) => [
                  department.id,
                  department.name,
                ]),
              ]}
            />

            <SelectField
              label="Designation"
              name="designationId"
              value={form.designationId}
              onChange={handleChange}
              required
              disabled={!form.departmentId}
              options={[
                [
                  "",
                  form.departmentId
                    ? "Select Designation"
                    : "Select a department first",
                ],
                ...designations.map((designation) => [
                  designation.id,
                  designation.name,
                ]),
              ]}
            />

            <Field
              label="Joining Date"
              type="date"
              name="joiningDate"
              value={form.joiningDate}
              onChange={handleChange}
              required
            />

            <SelectField
              label="Employment Type"
              name="employmentType"
              value={form.employmentType}
              onChange={handleChange}
              options={[
                ["PERMANENT", "Permanent"],
                ["CONTRACT", "Contract"],
                ["INTERN", "Intern"],
                ["TEMPORARY", "Temporary"],
              ]}
            />
          </div>
        </section>

        {canViewSalary ? <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiDollarSign size={20} />}
            title="Salary, Payroll & Deductions"
            description="These values are used by monthly and off-cycle payroll generation."
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <SelectField
              label="Salary Type"
              name="salaryType"
              value={form.salaryType}
              onChange={handleChange}
              options={[
                ["", "Not configured"],
                ["MONTHLY", "Monthly Salary"],
                ["DAILY", "Daily Wage"],
              ]}
            />

            <Field
              label="Basic Salary"
              type="number"
              name="basicSalary"
              value={form.basicSalary}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="Example: 23000"
            />
            <Field label="Annual CTC (optional)" type="number" name="ctc" value={form.ctc} onChange={handleChange} min="0" step="0.01" placeholder="Leave blank if not configured" />

            <SelectField
              label="Payment Mode"
              name="paymentMode"
              value={form.paymentMode}
              onChange={handleChange}
              options={[
                ["", "Not configured"],
                ["BANK_TRANSFER", "Bank Transfer"],
                ["CASH", "Cash"],
                ["CHEQUE", "Cheque"],
              ]}
            />

            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-sm font-medium text-gray-700">
                Salary calculation note
              </p>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                Monthly employees are prorated by days in the month. Daily
                employees receive the basic salary for each payable day.
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ToggleCard
              name="overtimeEligible"
              checked={form.overtimeEligible}
              onChange={handleChange}
              title="Eligible for overtime pay"
              description="Enable only when this employee should receive overtime payment."
            >
              <Field
                label="Overtime Rate per Hour"
                type="number"
                name="overtimeRate"
                value={form.overtimeRate}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="Leave blank for auto-calculation"
                disabled={!form.overtimeEligible}
              />
            </ToggleCard>

            <ToggleCard
              name="earlyExitDeductionEligible"
              checked={form.earlyExitDeductionEligible}
              onChange={handleChange}
              title="Deduct for early exit / undertime"
              description="Enable when early departures should reduce payroll."
            >
              <Field
                label="Early Exit Rate per Hour"
                type="number"
                name="earlyExitDeductionRate"
                value={form.earlyExitDeductionRate}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="Leave blank for auto-calculation"
                disabled={!form.earlyExitDeductionEligible}
              />
            </ToggleCard>
          </div>
        </section>

        : <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm"><h2 className="font-semibold">Salary, Payroll &amp; Deductions</h2><p className="mt-3 text-gray-500">Private</p></section>}

        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiClock size={20} />}
            title="Work Schedule & Holidays"
            description="Used for attendance, overtime, early-exit calculations and progressive paid-holiday entitlement."
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <Field
              label="Shift Type"
              name="shiftType"
              value={form.shiftType}
              onChange={handleChange}
              placeholder="Example: General Shift"
            />

            <Field
              label="Shift Start Time"
              type="time"
              name="shiftStartTime"
              value={form.shiftStartTime}
              onChange={handleChange}
            />

            <Field
              label="Shift End Time"
              type="time"
              name="shiftEndTime"
              value={form.shiftEndTime}
              onChange={handleChange}
            />

            <Field
              label="Break Duration (Minutes)"
              type="number"
              name="breakDurationMinutes"
              value={form.breakDurationMinutes}
              onChange={handleChange}
              min="0"
              step="1"
              placeholder="Example: 60"
            />

            <Field
              label="Late Grace Time (Minutes)"
              type="number"
              name="graceMinutes"
              value={form.graceMinutes}
              onChange={handleChange}
              min="0"
              step="1"
              placeholder="Example: 10"
            />
          </div>

          <div className="mt-6 rounded-2xl border border-primary-100 bg-primary-50/40 p-5">
            <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-700">
                  Central policy assignment
                </p>
                <h3 className="mt-1 font-semibold text-gray-900">
                  Leave & Holiday Policy
                </h3>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  This replaces the old fixed monthly holiday allowance. Payroll
                  unlocks paid holiday days progressively from qualifying
                  attendance.
                </p>
              </div>
              {selectedAttendanceHolidayPolicy ? (
                <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-800 shadow-sm">
                  Policy assigned
                </span>
              ) : null}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
              <SelectField
                label="Attendance / Holiday Policy"
                name="attendanceHolidayPolicyId"
                value={form.attendanceHolidayPolicyId}
                onChange={handleChange}
                options={[
                  ["", "No progressive holiday policy"],
                  ...attendanceHolidayPolicies.map((policy) => [
                    policy.id,
                    `${policy.name} (${policy.code})`,
                  ]),
                ]}
              />

              <Field
                label="Policy Effective From"
                type="date"
                name="attendanceHolidayPolicyEffectiveFrom"
                value={form.attendanceHolidayPolicyEffectiveFrom}
                onChange={handleChange}
                disabled={!form.attendanceHolidayPolicyId}
              />
            </div>

            {selectedAttendanceHolidayPolicy ? (
              <div className="mt-5 rounded-xl border border-primary-100 bg-white p-4">
                <p className="text-sm font-semibold text-gray-800">
                  {selectedAttendanceHolidayPolicy.name}
                </p>
                {selectedAttendanceHolidayPolicy.description ? (
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedAttendanceHolidayPolicy.description}
                  </p>
                ) : null}
                <p className="mt-3 text-xs text-gray-500">
                  Present and late count as 1 qualifying day; half day counts as
                  0.5 day
                  {selectedAttendanceHolidayPolicy.countPaidLeaveAsQualifying
                    ? "; approved paid leave also counts."
                    : "."}
                </p>
                {selectedPolicyTiers.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {selectedPolicyTiers.map((tier) => (
                      <span
                        key={
                          tier.id ||
                          `${tier.minimumQualifyingDays}-${tier.holidayDays}`
                        }
                        className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800"
                      >
                        {tier.minimumQualifyingDays} days → {tier.holidayDays}{" "}
                        paid holiday{Number(tier.holidayDays) === 1 ? "" : "s"}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="mt-4 text-sm text-amber-700">
                No policy is assigned. Absences remain unpaid unless covered by
                another approved leave rule.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiDollarSign size={20} />}
            title="POS Commission"
            description="Commission applies to eligible POS item sales where this employee is selected as the sales agent."
          />

          <div className="mt-6 max-w-md">
            <Field
              label="Default Commission (%)"
              type="number"
              name="commissionPercent"
              value={form.commissionPercent}
              onChange={handleChange}
              min="0"
              max="100"
              step="0.01"
              placeholder="Example: 5"
            />

            <p className="mt-2 text-xs text-gray-400">
              Leave blank or enter 0 to disable the default POS commission.
              Product-level rules can still be configured separately.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiCreditCard size={20} />}
            title="Bank Details"
            description="Sensitive account and IFSC values remain encrypted. Leave a field blank to preserve its current value."
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              label="Bank Name"
              name="bankName"
              value={form.bankName}
              onChange={handleChange}
              placeholder="Example: HDFC Bank"
            />

            <Field
              label="Account Holder Name"
              name="accountHolderName"
              value={form.accountHolderName}
              onChange={handleChange}
            />

            <div>
              <Field
                label="New Account Number"
                name="accountNumber"
                value={form.accountNumber}
                onChange={handleChange}
                placeholder={
                  employee?.bankAccount?.accountNumberLast4
                    ? `Current account ends in ${employee.bankAccount.accountNumberLast4}`
                    : "Enter account number"
                }
              />

              <p className="mt-2 text-xs text-gray-400">
                Leave blank to keep the current account number unchanged.
              </p>
            </div>

            <div>
              <Field
                label="New IFSC Code"
                name="ifscCode"
                value={form.ifscCode}
                onChange={handleChange}
                placeholder="Example: HDFC0001234"
              />

              <p className="mt-2 text-xs text-gray-400">
                Leave blank to keep the current IFSC code unchanged.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiFileText size={20} />}
            title="Identity Documents"
            description="Document numbers are encrypted. Enter a value only when adding or replacing it."
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <Field
                label="Aadhaar Number"
                name="aadhaarNumber"
                value={form.aadhaarNumber}
                onChange={handleChange}
                placeholder={
                  aadhaarDocument?.documentNumberLast4
                    ? `Current Aadhaar ends in ${aadhaarDocument.documentNumberLast4}`
                    : "Enter Aadhaar number"
                }
              />

              <p className="mt-2 text-xs text-gray-400">
                {aadhaarDocument
                  ? "Leave blank to retain the existing Aadhaar record."
                  : "Optional."}
              </p>
            </div>

            <div>
              <Field
                label="PAN Number"
                name="panNumber"
                value={form.panNumber}
                onChange={handleChange}
                placeholder={
                  panDocument?.documentNumberLast4
                    ? `Current PAN ends in ${panDocument.documentNumberLast4}`
                    : "Enter PAN number"
                }
              />

              <p className="mt-2 text-xs text-gray-400">
                {panDocument
                  ? "Leave blank to retain the existing PAN record."
                  : "Optional."}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <SectionTitle
            icon={<FiShield size={20} />}
            title="ERP Dashboard Access"
            description="All employees retain mobile self-service access. Enable ERP access only for admin/dashboard users."
          />

          <label className="mt-6 flex items-center gap-3 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              name="grantErpAccess"
              checked={form.grantErpAccess}
              onChange={handleChange}
              className="h-4 w-4 accent-primary-700"
            />
            Give ERP dashboard access
          </label>

          {form.grantErpAccess ? (
            <div className="mt-5">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                ERP Roles
              </label>

              <select
                multiple
                name="erpRoleIds"
                value={form.erpRoleIds}
                onChange={handleChange}
                className="min-h-36 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-700"
              >
                {erpRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name} ({role.code})
                  </option>
                ))}
              </select>

              <p className="mt-2 text-xs text-gray-400">
                Hold Command on Mac or Control on Windows to select multiple
                roles.
              </p>
            </div>
          ) : null}
        </section>

        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-200 bg-white/95 px-5 py-4 backdrop-blur md:left-64">
          <div className="mx-auto flex max-w-7xl justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(`/admindashboard/employees/${id}`)}
              className="rounded-xl border border-gray-200 px-5 py-3 font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-5 py-3 font-medium text-white hover:bg-primary-800 disabled:opacity-60"
            >
              <FiSave />
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ icon, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-primary-700">{icon}</div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

function ToggleCard({ name, checked, onChange, title, description, children }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        checked
          ? "border-primary-200 bg-primary-50/40"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          className="mt-1 h-4 w-4 accent-primary-700"
        />

        <span>
          <span className="block font-semibold text-gray-900">{title}</span>
          <span className="mt-1 block text-sm leading-5 text-gray-500">
            {description}
          </span>
        </span>
      </label>

      <div className="mt-5">{children}</div>
    </div>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </label>

      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-700 disabled:bg-gray-50"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={`${optionValue}-${optionLabel}`} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}

function Field({
  label,
  type = "text",
  name,
  value,
  onChange,
  required = false,
  min,
  max,
  step,
  placeholder,
  disabled = false,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-700 disabled:cursor-not-allowed disabled:bg-gray-100"
      />
    </div>
  );
}
